import type { BootstrapData, GameEvent } from '../api/types';

export const ONBOARDING_MAX_AGE_DAYS = 14;
export const STEP_COIN = 10;
export const DONE_COIN = 50;

export type OnboardingStepKey =
  | 'first_op'
  | 'categories'
  | 'hot_category'
  | 'first_chest'
  | 'first_goal';

export interface OnboardingStep {
  key: OnboardingStepKey;
  title: string;
  done: boolean;
}

const STEP_TITLES: Record<OnboardingStepKey, string> = {
  first_op: 'Первая запись',
  categories: 'Сверка категорий',
  hot_category: 'Горячая категория',
  first_chest: 'Первый сундук',
  first_goal: 'Первая цель',
};

const STEP_ORDER: OnboardingStepKey[] = [
  'first_op',
  'categories',
  'hot_category',
  'first_chest',
  'first_goal',
];

/** `ob_<key>` — идентификатор события для шага онбординга. */
export function stepEventId(key: OnboardingStepKey): string {
  return `ob_${key}`;
}

/** created_at (ISO или SQLite "YYYY-MM-DD HH:MM:SS" UTC) → мс epoch. Мусор → null. */
function createdAtMs(createdAt: string): number | null {
  let s = createdAt.trim();
  if (!s) return null;
  if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Возраст household в днях: минимальный created_at НЕ-imp_* операций.
 * Нет таких операций → 0.
 *
 * Источник по умолчанию — `aggregates.first_op_created_at` (минимум по всей
 * таблице, план 3a): окно `operations` может не содержать первую запись, и без
 * агрегата годовалый household выглядел бы новорождённым. Окно всё равно
 * просматривается — локальная оптимистичная запись может быть старше агрегата.
 */
export function householdAgeDays(data: BootstrapData, now: Date = new Date()): number {
  const fromAgg = data.aggregates?.first_op_created_at;
  let minMs: number | null = fromAgg ? createdAtMs(fromAgg) : null;

  for (const op of data.operations) {
    if (op.id.startsWith('imp_')) continue;
    const ms = createdAtMs(op.created_at);
    if (ms === null) continue;
    if (minMs === null || ms < minMs) minMs = ms;
  }

  if (minMs === null) return 0;
  return Math.floor((now.getTime() - minMs) / 86_400_000);
}

/** Завершён ли онбординг: есть хотя бы одно событие `onboarding_done`. */
export function onboardingDone(events: GameEvent[]): boolean {
  return events.some((e) => e.type === 'onboarding_done');
}

const VALID_STEP_KEYS = new Set<string>([
  'first_op',
  'categories',
  'hot_category',
  'first_chest',
  'first_goal',
]);

/**
 * Множество ключей шагов онбординга, за которые уже выданы монеты.
 * Источник: события `onboarding_step`, ключ хранится в поле `week`.
 * Невалидные значения week (не из OnboardingStepKey) игнорируются.
 */
export function claimedSteps(events: GameEvent[]): Set<OnboardingStepKey> {
  const result = new Set<OnboardingStepKey>();
  for (const e of events) {
    if (e.type === 'onboarding_step' && VALID_STEP_KEYS.has(e.week)) {
      result.add(e.week as OnboardingStepKey);
    }
  }
  return result;
}

/** Детектирует 5 шагов онбординга из BootstrapData. Порядок фиксирован. */
export function detectSteps(data: BootstrapData): OnboardingStep[] {
  const { settings, categories, goals, operations, game_events, aggregates } = data;

  const doneMap: Record<OnboardingStepKey, boolean> = {
    // Агрегат знает всю таблицу, окно operations — нет (план 3a). Проверку по
    // окну оставляем: оптимистичная запись ещё не попала в серверный агрегат.
    first_op:
      aggregates?.first_op_created_at != null ||
      operations.some((op) => !op.id.startsWith('imp_')),
    categories: Boolean(settings.categories_reviewed),
    hot_category: categories.some((c) => c.is_hot === 1),
    first_chest: game_events.some((e) => e.type === 'chest_open'),
    first_goal: goals.length > 0,
  };

  return STEP_ORDER.map((key) => ({
    key,
    title: STEP_TITLES[key],
    done: doneMap[key],
  }));
}

/**
 * Показывать ли карточку онбординга:
 * - возраст household строго < 14 дней
 *   (age=0 → нет ни одной не-imp операции → показываем, чтобы стимулировать
 *   первую запись; спека §7)
 * - онбординг НЕ завершён
 */
export function showOnboarding(data: BootstrapData, now: Date = new Date()): boolean {
  const age = householdAgeDays(data, now);
  if (age >= ONBOARDING_MAX_AGE_DAYS) return false;
  return !onboardingDone(data.game_events);
}
